import React, { useState, useEffect } from 'react';

// ── Helpers ───────────────────────────────────────────────────────────────────
export function getScoreBadge(s) {
  if (s >= 80) return { label: 'Excellent Probability',    bg: 'rgba(27,140,46,0.07)',   color: '#1b8c2e', arrow: '↗' };
  if (s >= 65) return { label: 'Good Probability',         bg: 'rgba(0,53,133,0.07)',    color: '#2563eb', arrow: '↗' };
  if (s >= 50) return { label: 'Fair · Needs Work',        bg: 'rgba(154,103,0,0.07)',   color: '#9A6700', arrow: '↘' };
  return             { label: 'Critical · Major Revision', bg: 'rgba(181,50,91,0.07)',   color: '#B5325B', arrow: '↘' };
}

// ── Ring color logic — mirrors ScoreOverview exactly ─────────────────────────
const getRingColor = (n) => {
  if (n >= 85) return '#10F090';   // Excellent — emerald
  if (n >= 70) return '#22D3EE';   // Good      — cyan
  if (n >= 50) return '#F59E0B';   // Needs Work — amber
  return '#B5325B';                 // Critical  — redish-pink
};

const getRingGlow = (n) => {
  if (n >= 85) return 'rgba(16,240,144,0.45)';
  if (n >= 70) return 'rgba(34,211,238,0.45)';
  if (n >= 50) return 'rgba(245,158,11,0.45)';
  return 'rgba(255,87,51,0.45)';
};

const DIMS = [
  { key: 'narrative',  label: 'Narrative'  },
  { key: 'visual',     label: 'Visual'     },
  { key: 'data',       label: 'Data'       },
  { key: 'structure',  label: 'Structure'  },
  { key: 'persuasion', label: 'Persuasion' },
];

// ── Animated Ring ─────────────────────────────────────────────────────────────
const Ring = ({ score, size = 172 }) => {
  const [v, setV] = useState(0);
  const [opacity, setOpacity] = useState(1);

  const R    = 80 * (size / 200);
  const CIRC = 2 * Math.PI * R;
  const SW   = 6 * (size / 200);

  useEffect(() => {
    const phases = [
      { target: Math.round(score * 0.94), duration: 2500, glitch: false },
      { target: Math.round(score * 0.78), duration: 400,  glitch: true  },
      { target: Math.round(score * 0.92), duration: 300,  glitch: true  },
      { target: score,                    duration: 1200, glitch: false },
    ];
    let cancelled = false, current = 0;
    const runPhase = (idx) => {
      if (idx >= phases.length || cancelled) return;
      const { target, duration, glitch } = phases[idx];
      const start = current, diff = target - start, t0 = performance.now();
      const tick = (now) => {
        if (cancelled) return;
        const progress = Math.min((now - t0) / duration, 1);
        const eased = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        const val = Math.round(start + diff * eased);
        setV(val); current = val;
        if (glitch && Math.random() > 0.7) { setOpacity(0.3); setTimeout(() => setOpacity(1), 50); }
        if (progress < 1) { requestAnimationFrame(tick); }
        else { current = target; setTimeout(() => runPhase(idx + 1), idx === phases.length - 1 ? 0 : 100); }
      };
      requestAnimationFrame(tick);
    };
    runPhase(0);
    return () => { cancelled = true; };
  }, [score]);

  const ringColor = getRingColor(v);
  const ringGlow  = getRingGlow(v);
  const offset    = CIRC * (1 - v / 100);

  return (
    <div style={{ position: 'relative', width: size, height: size, margin: '0 auto 22px' }}>
      {/* Ambient glow — color-matched to ring */}
      <div style={{
        position: 'absolute', inset: -28 * (size / 172), borderRadius: '50%',
        background: `radial-gradient(circle, ${ringGlow.replace('0.45', '0.04')}, transparent 65%)`,
        animation: 'db-breathe 5s ease-in-out infinite', pointerEvents: 'none',
      }} />
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <defs>
          <filter id="ring-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2" result="blurAlpha" />
            <feFlood floodColor={ringColor} floodOpacity="0.35" result="color" />
            <feComposite in="color" in2="blurAlpha" operator="in" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={R} fill="none" stroke="var(--ring-track)" strokeWidth={SW} />
        <circle cx={size / 2} cy={size / 2} r={R}
          fill="none"
          stroke={ringColor}
          strokeWidth={SW}
          strokeLinecap="round"
          strokeDasharray={CIRC}
          strokeDashoffset={offset}
          filter="url(#ring-glow)"
          style={{ transition: 'stroke-dashoffset 0.1s, stroke 0.4s' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
       <span style={{
          fontFamily: 'inherit',
          fontSize: 54 * (size / 172), fontWeight: 800,
          color: ringColor,
          lineHeight: 1, letterSpacing: '-0.04em',
          opacity, transition: 'opacity 0.05s, color 0.4s',
          textShadow: 'none',
          filter: 'none',
        }}>{v}</span>
        <span style={{
          fontFamily: 'inherit',
          fontSize: 9 * (size / 172), fontWeight: 500,
          color: 'var(--label)', letterSpacing: '0.14em',
          textTransform: 'uppercase', marginTop: 3,
        }}>DecX Score™</span>
      </div>
    </div>
  );
};

// ── Bar Chart ─────────────────────────────────────────────────────────────────
const BarChart = ({ subScores }) => {
  const yLabels = [100, 75, 50, 25, 0];
  const getSoftPct = (val) => {
    if (val <= 0) return 0;
    return Math.round(Math.max(Math.min(90 - val * 0.68, 50), 28));
  };

  return (
    <div style={{ flex: 1, position: 'relative', paddingLeft: 40, paddingBottom: 32, minHeight: 260 }}>
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 32,
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        width: 36, pointerEvents: 'none', zIndex: 1,
      }}>
        {yLabels.map(t => (
          <div key={t} style={{
            fontFamily: 'inherit',
            fontSize: 10, fontWeight: 500, color: 'var(--text-4)', textAlign: 'right', paddingRight: 10, lineHeight: 1,
          }}>{t}</div>
        ))}
      </div>
      <div style={{
        position: 'absolute', left: 40, right: 0, top: 0, bottom: 32,
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        pointerEvents: 'none', zIndex: 1,
      }}>
        {yLabels.map(t => (
          <div key={t} style={{ width: '100%', height: '0.5px', background: 'var(--divider)' }} />
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', position: 'relative', zIndex: 2, height: 'calc(100% - 32px)' }}>
        {DIMS.map(d => {
          const val      = Math.max(subScores?.[d.key] ?? 0, 0);
          const softPct  = getSoftPct(val);
          const fillPct  = 100 - softPct;
          const barColor = getRingColor(val);
          const barGlow  = getRingGlow(val);

          return (
            <div key={d.key} style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', height: '100%', justifyContent: 'flex-end', position: 'relative',
            }}>
              <div
                style={{
                  width: 48, height: `${val}%`,
                  borderRadius: '8px 8px 3px 3px',
                  overflow: 'hidden',
                  display: 'flex', flexDirection: 'column-reverse',
                  cursor: 'pointer', transition: 'transform 0.3s var(--ease)', flexShrink: 0,
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scaleY(1.03) translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; }}
                title={`${val} / 100`}
              >
                {/* Soft base */}
                <div style={{
                  width: '100%', height: `${softPct}%`,
                  background: `${barColor}18`,
                  flexShrink: 0,
                }} />
                {/* Filled */}
                <div style={{
                  width: '100%', height: `${fillPct}%`,
                  background: barColor,
                  boxShadow: `0 0 10px ${barGlow}`,
                  flexShrink: 0,
                }} />
              </div>
              <div style={{
                position: 'absolute', bottom: -28, left: '50%', transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
                fontFamily: 'inherit', fontSize: 11, fontWeight: 600,
                color: 'var(--text-3)', letterSpacing: '0.01em',
              }}>{d.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── AuditReportViewer (Hero: Score Card + Breakdown) ─────────────────────────
const AuditReportViewer = ({ rd, activeReport, filterOpts, selectedFilter, setSelectedFilter, onViewReport }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const badge = rd ? getScoreBadge(rd.score) : null;

  if (!rd) return null;

  return (
    <div className="db-hero">
      {/* Score Card */}
      <div className="score-card">
        <div className="sc-title">
          DecX AI Score™
          <span className="info-wrap">
            <span className="info-ico">
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="7.25" stroke="currentColor" strokeWidth="1.5"/>
                <rect x="7.25" y="7" width="1.5" height="5" rx="0.75" fill="currentColor"/>
                <rect x="7.25" y="4" width="1.5" height="1.5" rx="0.75" fill="currentColor"/>
              </svg>
            </span>
            <span className="info-tip">
              <span style={{display:'block',fontFamily:'inherit',fontSize:11,fontWeight:600,color:'#fff',textTransform:'none',letterSpacing:'normal',marginBottom:5}}>DecX AI Score™</span>
              <span style={{display:'block',textTransform:'none',letterSpacing:'normal',marginBottom:4}}>0–100 investor-readiness score across 5 dimensions.</span>
              <span className="info-tip-row"><span className="info-tip-dot" style={{background:'#10F090'}}/><span style={{textTransform:'none',letterSpacing:'normal'}}>85–100 · Excellent</span></span>
              <span className="info-tip-row"><span className="info-tip-dot" style={{background:'#22D3EE'}}/><span style={{textTransform:'none',letterSpacing:'normal'}}>70–84 · Good</span></span>
              <span className="info-tip-row"><span className="info-tip-dot" style={{background:'#F59E0B'}}/><span style={{textTransform:'none',letterSpacing:'normal'}}>50–69 · Needs Work</span></span>
              <span className="info-tip-row"><span className="info-tip-dot" style={{background:'#B5325B'}}/><span style={{textTransform:'none',letterSpacing:'normal'}}>0–49 · Critical</span></span>
            </span>
          </span>
        </div>

        <Ring score={rd.score} size={172} />

        <div className="sc-badge" style={{ background: badge.bg, color: badge.color }}>
          <span className="sc-badge-dot" />{badge.label}
        </div>

        <p className="sc-desc">
          {rd.score < 50
            ? 'Critical issues found — major revision required across key areas.'
            : rd.score < 65
              ? 'Fair foundation — significant improvements needed to close.'
              : rd.score < 80
                ? 'Good deck — targeted refinements will maximize investor appeal.'
                : 'Exceptional deck — investor-ready with minor polish.'}
        </p>

        <div className="sc-tags">
          {rd.industry && <span className="sc-tag">{rd.industry}</span>}

          {/* ── Premium PAID badge ── */}
          {rd.reportType?.toLowerCase() === 'paid' ? (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '4px 10px', borderRadius: 100,
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
              border: '1px solid rgba(250,204,21,0.30)',
              boxShadow: '0 0 8px rgba(250,204,21,0.15), inset 0 1px 0 rgba(255,255,255,0.07)',
              position: 'relative', overflow: 'hidden',
            }}>
              <style>{`@keyframes paid-shimmer { 0%,100% { left: -60% } 50% { left: 120% } }`}</style>
              <span style={{
                position: 'absolute', top: 0, left: '-100%',
                width: '60%', height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)',
                animation: 'paid-shimmer 3s ease-in-out infinite',
                pointerEvents: 'none',
              }} />
              <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#facc15', boxShadow: '0 0 6px rgba(250,204,21,0.9)', flexShrink: 0 }} />
              <svg width={12} height={12} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                <path d="M2 20h20M4 20L2 9l6 4 4-7 4 7 6-4-2 11H4z" stroke="#22c55e" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="2" cy="9" r="1.5" fill="#22c55e"/>
                <circle cx="12" cy="2" r="1.5" fill="#22c55e"/>
                <circle cx="22" cy="9" r="1.5" fill="#22c55e"/>
              </svg>
              <span style={{ fontSize: 11, fontWeight: 500, color: '#ffffff', fontFamily: 'inherit' }}>Paid</span>
            </span>
          ) : (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '4px 10px', borderRadius: 100,
              background: 'linear-gradient(135deg, #052e16 0%, #064e3b 50%, #065f46 100%)',
              border: '1px solid rgba(34,197,94,0.30)',
              boxShadow: '0 0 8px rgba(34,197,94,0.12), inset 0 1px 0 rgba(255,255,255,0.07)',
              position: 'relative', overflow: 'hidden',
            }}>
              <span style={{
                position: 'absolute', top: 0, left: '-100%',
                width: '60%', height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)',
                animation: 'paid-shimmer 3s ease-in-out infinite',
                pointerEvents: 'none',
              }} />
              <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#4ade80', boxShadow: '0 0 6px rgba(74,222,128,0.9)', flexShrink: 0 }} />
              <svg width={11} height={11} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                <rect x="3" y="11" width="18" height="11" rx="2" stroke="#4ade80" strokeWidth={2}/>
                <path d="M7 11V7a5 5 0 0 1 9.9-1" stroke="#4ade80" strokeWidth={2} strokeLinecap="round"/>
              </svg>
              <span style={{ fontSize: 11, fontWeight: 500, color: '#ffffff', fontFamily: 'inherit' }}>Free</span>
            </span>
          )}

          {rd.totalSlides > 0 && <span className="sc-tag">{rd.totalSlides} slides</span>}
        </div>

        <button className="sc-btn" onClick={onViewReport}>
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
          </svg>
          View Detailed Report
        </button>
      </div>

      {/* Breakdown Card */}
      <div className="breakdown-card">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28, gap: 12 }}>
          <div>
            <div className="bk-title">DecX AI Score™ Breakdown</div>
          </div>
          {/* Dropdown */}
          <div style={{ position: 'relative', flexShrink: 0 }}
            onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDropdownOpen(false); }}>
            <button onClick={() => setDropdownOpen(o => !o)} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'var(--input-bg)', border: '0.5px solid var(--card-border)',
              borderRadius: 9, padding: '7px 12px', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 12, fontWeight: 500,
              color: 'var(--text-2)', outline: 'none', whiteSpace: 'nowrap',
              maxWidth: 240, overflow: 'hidden', transition: 'border-color 0.15s',
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-4)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
                {filterOpts.find(o => o.value === selectedFilter)?.label || 'Latest Report'}
              </span>
              <svg width="10" height="10" viewBox="0 0 10 6" fill="none" stroke="var(--text-4)" strokeWidth={1.5} strokeLinecap="round"
                style={{ marginLeft: 2, flexShrink: 0, transform: dropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
                <path d="M1 1l4 4 4-4"/>
              </svg>
            </button>
            {dropdownOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 4px)', right: 0,
                background: 'var(--card)', border: '0.5px solid var(--card-border)',
                borderRadius: 12, boxShadow: 'var(--card-shadow-hover)',
                zIndex: 50, minWidth: 240, overflow: 'hidden',
              }}>
                {filterOpts.map(o => (
                  <button key={o.value} tabIndex={0}
                    onClick={() => { setSelectedFilter(o.value); setDropdownOpen(false); }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', padding: '9px 14px',
                      fontFamily: 'inherit', fontSize: 12,
                      fontWeight: o.value === selectedFilter ? 700 : 500,
                      color: o.value === selectedFilter ? 'var(--blue)' : 'var(--text-2)',
                      background: o.value === selectedFilter ? 'var(--blue-soft)' : 'transparent',
                      border: 'none', cursor: 'pointer', borderBottom: '0.5px solid var(--divider)',
                    }}
                    onMouseEnter={e => { if (o.value !== selectedFilter) e.currentTarget.style.background = 'var(--input-bg)'; }}
                    onMouseLeave={e => { if (o.value !== selectedFilter) e.currentTarget.style.background = 'transparent'; }}>
                    {o.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <BarChart subScores={rd.subScores} />
      </div>
    </div>
  );
};

export default AuditReportViewer;